import { isIOS } from "@/lib/installPrompt";

// iOS / iPadOS WKWebView: the native <video controls> bar drops the fullscreen button
// (element fullscreen is not offered inside the app shell), so the theater draws its own.
export const needsFullscreenButton = () => {
  try { return isIOS(); } catch { return false; }
};

// Video-only fullscreen first (AVKit player, works on iPhone AND iPad, HLS included),
// then the standard element API where available. Returns false when nothing worked.
export const enterVideoFullscreen = (video) => {
  if (!video) return false;
  if (typeof video.webkitEnterFullscreen === "function") {
    try { video.webkitEnterFullscreen(); return true; } catch { /* not ready yet → fall through */ }
  }
  const req = video.requestFullscreen || video.webkitRequestFullscreen;
  if (typeof req === "function") {
    try { const p = req.call(video); if (p && p.catch) p.catch(() => {}); return true; } catch { /* unsupported */ }
  }
  return false;
};

export const exitVideoFullscreen = () => {
  const el = document.fullscreenElement || document.webkitFullscreenElement;
  if (el) {
    const ex = document.exitFullscreen || document.webkitExitFullscreen;
    try { const p = ex?.call(document); if (p && p.catch) p.catch(() => {}); } catch { /* noop */ }
    return true;
  }
  return false;
};

// ---------------------------------------------------------------------------
// Vrai plein écran « façon YouTube » : à l'entrée, une vidéo horizontale (ou un lecteur YouTube)
// force le PAYSAGE même si la rotation auto du téléphone est coupée ; une vidéo verticale reste
// en portrait. À la sortie, on revient à l'orientation d'AVANT puis on libère la rotation.
// ---------------------------------------------------------------------------
let isNativeApp = false;
let ScreenOrientation = null;
let fsActive = false;
let prevType = null;

// Horizontal wanted? <video> whose frame is wider than tall (unknown → yes), <iframe> (YouTube) → yes.
export const wantsLandscape = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toUpperCase();
  if (tag === "VIDEO") return !el.videoWidth || !el.videoHeight || el.videoWidth >= el.videoHeight;
  if (tag === "IFRAME") return true;
  const v = typeof el.querySelector === "function" ? el.querySelector("video") : null;
  return v ? wantsLandscape(v) : true;
};

const currentType = async () => {
  try {
    if (isNativeApp && ScreenOrientation) return (await ScreenOrientation.orientation()).type;
    return window.screen?.orientation?.type || "portrait-primary";
  } catch { return "portrait-primary"; }
};

const lock = async (type) => {
  try {
    if (isNativeApp && ScreenOrientation) await ScreenOrientation.lock({ orientation: type });
    else await window.screen?.orientation?.lock?.(type);
  } catch { /* PC / navigateur sans verrou → rien */ }
};

const unlock = async () => {
  try {
    if (isNativeApp && ScreenOrientation) await ScreenOrientation.unlock();
    else window.screen?.orientation?.unlock?.();
  } catch { /* noop */ }
};

export const onFullscreenEnter = async (el) => {
  if (fsActive) return;
  fsActive = true;
  prevType = await currentType();
  if (wantsLandscape(el)) await lock("landscape");
};

export const onFullscreenExit = async () => {
  if (!fsActive) return;
  fsActive = false;
  await lock(prevType && prevType.startsWith("landscape") ? "landscape" : "portrait");
  setTimeout(unlock, 700);
};

export const isFullscreenActive = () => fsActive;

// Installé une fois (App.js). Écoute le plein écran d'élément (Android WebView, PC, iframe YouTube)
// ET le plein écran vidéo AVKit d'iOS (webkitbeginfullscreen / webkitendfullscreen, capturés car
// non propagés). Le bouton Retour Android (NativeBackButton) quitte d'abord le plein écran.
export const installFullscreenOrientation = async () => {
  try {
    const { Capacitor } = await import("@capacitor/core");
    isNativeApp = !!Capacitor.isNativePlatform?.();
    if (isNativeApp) ScreenOrientation = (await import("@capacitor/screen-orientation")).ScreenOrientation;
  } catch { isNativeApp = false; }
  const onChange = () => {
    const el = document.fullscreenElement || document.webkitFullscreenElement;
    if (el) onFullscreenEnter(el); else onFullscreenExit();
  };
  const onBegin = (e) => onFullscreenEnter(e.target);
  const onEnd = () => onFullscreenExit();
  document.addEventListener("fullscreenchange", onChange);
  document.addEventListener("webkitfullscreenchange", onChange);
  document.addEventListener("webkitbeginfullscreen", onBegin, true);
  document.addEventListener("webkitendfullscreen", onEnd, true);
  return () => {
    document.removeEventListener("fullscreenchange", onChange);
    document.removeEventListener("webkitfullscreenchange", onChange);
    document.removeEventListener("webkitbeginfullscreen", onBegin, true);
    document.removeEventListener("webkitendfullscreen", onEnd, true);
  };
};
